import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { ArrowLeft, Loader2, Search, Sparkles, FileText, ExternalLink, Lock, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TokenSearch } from '@/components/coin-reviewer/TokenSearch';
import { VerdictCard } from '@/components/coin-reviewer/VerdictCard';
import { MarketOverview } from '@/components/coin-reviewer/MarketOverview';
import { AnalysisSection } from '@/components/coin-reviewer/AnalysisSection';
import { SwingTradeSetup } from '@/components/coin-reviewer/SwingTradeSetup';
import { CatalystsCard } from '@/components/coin-reviewer/CatalystsCard';
import { DetailedAnalysis } from '@/components/coin-reviewer/DetailedAnalysis';
import { FundamentalDDCard } from '@/components/coin-reviewer/FundamentalDDCard';
import { RiskProfileCard } from '@/components/coin-reviewer/RiskProfileCard';
import { ReviewErrorBoundary } from '@/components/coin-reviewer/ReviewErrorBoundary';
import { ChallengeTab } from '@/components/coin-reviewer/ChallengeTab';
import DisclaimerNote from '@/components/DisclaimerNote';
import type { ChallengeTokenInput } from '@/hooks/useChallengeToken';
import type { DeepAnalysis } from '@/hooks/useDeepAnalysis';
import { useDeepAnalysis } from '@/hooks/useDeepAnalysis';
import { usePublishAnalysis } from '@/hooks/usePublishAnalysis';
import { useAuth } from '@/contexts/AuthContext';
import { logRecentlyViewed } from '@/hooks/useUserTokens';
import { Link, useSearchParams } from 'react-router-dom';
import { AiGeneratedBadge } from '@/components/AiGeneratedBadge';
import { trackEvent, trackDebug } from '@/lib/trackEvent';
import { PostReviewCTA } from '@/components/coin-reviewer/PostReviewCTA';


const CoinReviewer = () => {
  const { analyzeToken, clearAnalysis, analysis, isAnalyzing, error, statusMessage } = useDeepAnalysis();
  const { publishAnalysis, isPublishing } = usePublishAnalysis();
  const { isAdmin, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchedCoin, setSearchedCoin] = useState<string | null>(null);
  const [publishedSlug, setPublishedSlug] = useState<string | null>(null);
  const autoLoadedCoinRef = useRef<string | null>(null);

  // Route-level `page_view` is already emitted globally; a second per-page
  // view event just doubled the stream. Keep it on the debug channel.
  useEffect(() => {
    trackDebug('coin_reviewer_page_view');
  }, []);

  useEffect(() => {
    // Accept either a CoinGecko id (`coin`) or a plain ticker/name (`symbol`);
    // the analyzer resolves tickers server-side via its mapping table.
    const coinParam = (searchParams.get('coin') || searchParams.get('symbol'))?.trim();
    if (!coinParam || autoLoadedCoinRef.current === coinParam) return;
    autoLoadedCoinRef.current = coinParam;
    setSearchedCoin(coinParam);
    setPublishedSlug(null);
    trackEvent('form_submit', { form_name: 'coin_review', coin: coinParam, entry: 'url_param' });
    trackEvent('coin_review_started', { coin: coinParam, entry: 'url_param' });
    analyzeToken(coinParam);
  }, [analyzeToken, searchParams]);

  // Conversion / failure outcomes for the review flow
  useEffect(() => {
    if (isAnalyzing || !analysis) return;
    logRecentlyViewed(user?.id, analysis.tokenInfo?.symbol ?? searchedCoin ?? '');
    trackEvent('coin_review_completed', {
      coin: analysis.tokenInfo?.symbol ?? searchedCoin,
      verdict: analysis.verdict,
      score: analysis.overallScore ?? null,
    });
    trackEvent('conversion', {
      type: 'coin_review_complete',
      coin: analysis.tokenInfo?.symbol ?? searchedCoin,
      verdict: analysis.verdict,
      score: analysis.overallScore ?? null,
    });
  }, [analysis, isAnalyzing, searchedCoin]);

  useEffect(() => {
    if (isAnalyzing || !error) return;
    trackEvent('error', { type: 'coin_review_failed', coin: searchedCoin, reason: error });
  }, [error, isAnalyzing, searchedCoin]);

  // Analyzer status updates tick several times per run (progress/plumbing).
  // Only a terminal failure status is a product event; the rest is debug.
  useEffect(() => {
    if (isAnalyzing || !statusMessage) return;
    const terminal = ['not_tracked', 'stale_data', 'failed', 'error'].includes(
      String(statusMessage.status)
    );
    if (!terminal) {
      trackDebug('coin_review_status', { coin: searchedCoin, status: statusMessage.status });
      return;
    }
    trackEvent('error', {
      type: 'coin_review_failed',
      coin: searchedCoin,
      reason: statusMessage.status,
    });
  }, [statusMessage, isAnalyzing, searchedCoin]);

  const handleCoinSelect = async (coinId: string) => {
    trackEvent('coin_review_started', { coin: coinId, entry: 'page' });
    setSearchedCoin(coinId);
    setPublishedSlug(null);
    await analyzeToken(coinId);
  };

  const handleNewSearch = () => {
    trackEvent('interaction', { type: 'coin_review_new_search', coin: searchedCoin });
    clearAnalysis();
    setSearchedCoin(null);
    setPublishedSlug(null);
    setSearchParams({});
  };

  const handlePublish = async () => {
    if (!analysis) return;
    trackEvent('interaction', { type: 'coin_review_publish', coin: searchedCoin });
    const result = await publishAnalysis(analysis);
    if (result.success && result.slug) {
      setPublishedSlug(result.slug);
      trackEvent('conversion', {
        type: 'coin_review_published',
        coin: searchedCoin,
        slug: result.slug,
      });
    }
  };


  return (
    <>
      <Helmet>
        <title>Coin Reviewer - AI-Powered Crypto Research | Crypto Tips Australia</title>
        <meta name="description" content="Get comprehensive AI-powered analysis on any cryptocurrency. Deep dive into technicals, fundamentals, sentiment, and get actionable trading insights." />
      </Helmet>

      <div className="min-h-screen bg-background">
        {/* Header */}
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Link to="/">
                  <Button variant="ghost" size="sm">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                </Link>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-6 h-6 text-primary" />
                  <h1 className="text-xl font-bold text-foreground">Coin Reviewer</h1>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {analysis && !publishedSlug && (
                  isAdmin ? (
                    <Button 
                      onClick={handlePublish} 
                      disabled={isPublishing}
                      size="sm"
                      className="gap-2 bg-gradient-to-r from-primary to-purple-500"
                    >
                      {isPublishing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Publishing...
                        </>
                      ) : (
                        <>
                          <FileText className="w-4 h-4" />
                          Publish as Article
                        </>
                      )}
                    </Button>
                  ) : (
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button 
                            size="sm"
                            variant="outline"
                            disabled
                            className="gap-2 opacity-60"
                          >
                            <Lock className="w-4 h-4" />
                            Publish as Article
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Admin access required to publish articles</p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )
                )}
                {publishedSlug && (
                  <Link to={`/article/${publishedSlug}`}>
                    <Button size="sm" variant="outline" className="gap-2 border-green-500/50 text-green-500 hover:bg-green-500/10">
                      <ExternalLink className="w-4 h-4" />
                      View Article
                    </Button>
                  </Link>
                )}
                {analysis && (
                  <Button onClick={handleNewSearch} variant="outline" size="sm">
                    <Search className="w-4 h-4 mr-2" />
                    New Search
                  </Button>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-8">
          {/* Search Section */}
          {!analysis && !isAnalyzing && !statusMessage && (
            <div className="max-w-3xl mx-auto text-center py-12">
              <div className="mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
                  <Sparkles className="w-8 h-8 text-primary" />
                </div>
                <h2 className="text-3xl font-bold text-foreground mb-3">
                  AI-Powered Coin Research
                </h2>
                <p className="text-lg text-muted-foreground max-w-xl mx-auto">
                  Get a comprehensive deep-dive analysis on any cryptocurrency. 
                  Our AI analyzes technicals, fundamentals, sentiment, and provides actionable trading insights.
                </p>
              </div>

              <TokenSearch onSelect={handleCoinSelect} isLoading={isAnalyzing} />

              {/* Features */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-12">
                {[
                  { icon: '📊', label: 'Technical Analysis' },
                  { icon: '💎', label: 'Fundamental Grade' },
                  { icon: '💬', label: 'Sentiment Score' },
                  { icon: '🎯', label: 'Trading Setup' },
                ].map((feature) => (
                  <div key={feature.label} className="p-4 bg-card rounded-lg border border-border">
                    <span className="text-2xl mb-2 block">{feature.icon}</span>
                    <span className="text-sm text-muted-foreground">{feature.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Loading State */}
          {isAnalyzing && (
            <div className="max-w-xl mx-auto text-center py-20">
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 mb-6">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-2">
                Analyzing {searchedCoin?.toUpperCase()}...
              </h3>
              <p className="text-muted-foreground">
                Our AI is gathering market data, analyzing technicals, and generating insights.
                This usually takes 10-15 seconds.
              </p>
              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span>Fetching market data</span>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && !isAnalyzing && (
            <div className="max-w-xl mx-auto text-center py-12">
              <Card className="border-destructive/50 bg-destructive/5">
                <CardContent className="pt-6">
                  <p className="text-destructive mb-4">{error}</p>
                  <Button onClick={handleNewSearch} variant="outline">
                    Try Another Coin
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Message State */}
          {statusMessage && !isAnalyzing && (
            <div className="max-w-xl mx-auto text-center py-12">
              <Card className="border-border bg-card">
                <CardContent className="pt-6">
                  <Badge variant="outline" className="mb-3">
                    {statusMessage.status === 'not_tracked' ? 'Coin not found' : statusMessage.status === 'data_stale' ? 'Data unavailable' : 'Insufficient data'}
                  </Badge>
                  <p className="text-muted-foreground mb-4">{statusMessage.message}</p>
                  <Button onClick={handleNewSearch} variant="outline">
                    Try Another Coin
                  </Button>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Analysis Results */}
          {analysis && !isAnalyzing && (
            <ReviewErrorBoundary resetKey={analysis.analyzedAt || searchedCoin} onReset={handleNewSearch}>
            <div className="space-y-6">
              <AiGeneratedBadge />
              {/* Published Banner */}
              {publishedSlug && (
                <Card className="bg-green-500/10 border-green-500/30">
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Badge className="bg-green-500/20 text-green-500 border-green-500/30">
                          Published
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          This analysis has been published as an article
                        </span>
                      </div>
                      <Link to={`/article/${publishedSlug}`}>
                        <Button size="sm" variant="ghost" className="gap-2 text-green-500 hover:text-green-400">
                          <ExternalLink className="w-4 h-4" />
                          View Article
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tabs: Verdict | Challenge */}
              <Tabs
                defaultValue="verdict"
                className="w-full"
                onValueChange={(value) =>
                  trackEvent('interaction', {
                    type: 'coin_review_tab',
                    tab: value,
                    coin: searchedCoin,
                  })
                }
              >

                <TabsList className="grid grid-cols-2 w-full max-w-md">
                  <TabsTrigger value="verdict">Verdict</TabsTrigger>
                  <TabsTrigger
                    value="challenge"
                    className="gap-2 text-warning data-[state=active]:text-warning"
                  >
                    <ShieldAlert className="w-4 h-4" />
                    Challenge
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="verdict" className="space-y-6 mt-6">
                  {/* Top Section: Verdict + Market Overview */}
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    <div className="lg:col-span-1">
                      <VerdictCard analysis={analysis} />
                    </div>
                    <div className="lg:col-span-2">
                      <MarketOverview analysis={analysis} />
                    </div>
                  </div>

                  {/* Risk Profile (AUD Sharpe + Drawdown) */}
                  {analysis.tokenInfo?.symbol && (
                    <RiskProfileCard symbol={analysis.tokenInfo.symbol} />
                  )}

                  {/* Analysis Sections */}
                  <AnalysisSection analysis={analysis} />

                  {/* Trading Setup & Risk */}
                  <SwingTradeSetup analysis={analysis} />

                  {/* Catalysts & Insights */}
                  <CatalystsCard analysis={analysis} />

                  {/* Fundamental Due Diligence */}
                  {analysis.fundamentalDueDiligence && (
                    <FundamentalDDCard dd={analysis.fundamentalDueDiligence} />
                  )}

                  {/* Detailed Analysis */}
                  <DetailedAnalysis analysis={analysis} />

                  {/* Post-review conversion CTAs */}
                  <PostReviewCTA
                    symbol={analysis.tokenInfo?.symbol ?? searchedCoin ?? 'this coin'}
                    verdict={analysis.verdict}
                    onNewSearch={handleNewSearch}
                  />

                  {analysis.dataQuality?.notice && (
                    <Card className="bg-secondary/30 border-border">
                      <CardContent className="py-3">
                        <p className="text-xs text-muted-foreground text-center">
                          {analysis.dataQuality.notice}
                        </p>
                      </CardContent>
                    </Card>
                  )}

                  {/* Disclaimer */}
                  <Card className="bg-warning/5 border-warning/20">
                    <CardContent className="py-4">
                      <DisclaimerNote
                        className="text-center"
                        prefix="This analysis is AI-generated."
                      />
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="challenge" className="mt-6">
                  <ChallengeTabContainer analysis={analysis} />
                </TabsContent>
              </Tabs>
            </div>
            </ReviewErrorBoundary>
          )}
        </main>
      </div>
    </>
  );
};

const ChallengeTabContainer = ({ analysis }: { analysis: DeepAnalysis }) => {
  const [active, setActive] = useState(false);
  useEffect(() => {
    setActive(true);
  }, []);

  const md = analysis.marketData;
  const input: ChallengeTokenInput = {
    symbol: analysis.tokenInfo?.symbol?.toUpperCase() ?? '',
    name: analysis.tokenInfo?.name ?? '',
    current_price: md?.price ?? null,
    price_change_24h: md?.priceChange24h ?? null,
    price_change_7d: md?.priceChange7d ?? null,
    volume_24h: md?.volume ?? null,
    market_cap: md?.marketCap ?? null,
    toss_score: null,
    toss_grade: null,
    available_on_exchanges: [],
    coinGeckoId: analysis.tokenInfo?.id ?? null,
  };

  return <ChallengeTab input={input} active={active} />;
};

export default CoinReviewer;

